import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

export interface Customer {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  parent?: Customer;
  children?: Customer[];
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Asset {
  id: string;
  label: string;
  floorPlan?: string;
  additional_info?: string;
  customerId: string;
  customer?: Customer;
  parentId?: string;
  parent?: Asset;
  children?: Asset[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Device {
  id: string;
  gatewayId: string;
  slaveId: number;
  gateway?: Gateway;
  name?: string;
  description?: string;
  deviceType?: 'energy' | 'water';
  customerId?: string;
  customer?: Customer;
  assetId?: string;
  asset?: Asset;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin';
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Gateway {
  id: string;
  name?: string;
  hardwareUuid: string;
  customerId?: string;
  customer?: Customer;
  assetId?: string;
  asset?: Asset;
  isOnline: boolean;
  lastSeen?: string;
  isRegistered: boolean;
  lastEnergyFetchTimestamp?: string;
  lastWaterFetchTimestamp?: string;
  isPaused: boolean;
  isDeleted: boolean;
  originalId?: string;
  fetchIntervalMs?: number;
  energyFetchIntervalMs?: number;
  waterFetchIntervalMs?: number;
  createdAt: string;
  updatedAt: string;
  devices?: Device[];
}

export interface HierarchyNode {
  id: string;
  name: string;
  type: 'customer' | 'asset' | 'device' | 'gateway';
  parentId?: string;
  children?: HierarchyNode[];
  data?: Customer | Asset | Device | Gateway;
  level?: number;
  isExpanded?: boolean;
}

export interface ManagementState {
  customers: Customer[];
  assets: Asset[];
  devices: Device[];
  gateways: Gateway[];
  hierarchy: HierarchyNode[];
  selectedEntity: HierarchyNode | null;
  loading: boolean;
  error: string | null;
  searchTerm: string;
  filters: {
    entityType: string;
    status: string;
    customerId?: string;
  };
}

type ManagementAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CUSTOMERS'; payload: Customer[] }
  | { type: 'SET_ASSETS'; payload: Asset[] }
  | { type: 'SET_DEVICES'; payload: Device[] }
  | { type: 'SET_GATEWAYS'; payload: Gateway[] }
  | { type: 'SET_HIERARCHY'; payload: HierarchyNode[] }
  | { type: 'SET_SELECTED_ENTITY'; payload: HierarchyNode | null }
  | { type: 'SET_SEARCH_TERM'; payload: string }
  | { type: 'SET_FILTERS'; payload: Partial<ManagementState['filters']> }
  | { type: 'ADD_CUSTOMER'; payload: Customer }
  | { type: 'UPDATE_CUSTOMER'; payload: Customer }
  | { type: 'DELETE_CUSTOMER'; payload: string }
  | { type: 'ADD_ASSET'; payload: Asset }
  | { type: 'UPDATE_ASSET'; payload: Asset }
  | { type: 'DELETE_ASSET'; payload: string }
  | { type: 'ADD_DEVICE'; payload: Device }
  | { type: 'UPDATE_DEVICE'; payload: Device }
  | { type: 'DELETE_DEVICE'; payload: string }
  | { type: 'ADD_GATEWAY'; payload: Gateway }
  | { type: 'UPDATE_GATEWAY'; payload: Gateway }
  | { type: 'DELETE_GATEWAY'; payload: string };

const initialState: ManagementState = {
  customers: [],
  assets: [],
  devices: [],
  gateways: [],
  hierarchy: [],
  selectedEntity: null,
  loading: false,
  error: null,
  searchTerm: '',
  filters: {
    entityType: 'all',
    status: 'active',
  },
};

function managementReducer(state: ManagementState, action: ManagementAction): ManagementState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.payload };
    case 'SET_ASSETS':
      return { ...state, assets: action.payload };
    case 'SET_DEVICES':
      return { ...state, devices: action.payload };
    case 'SET_GATEWAYS':
      return { ...state, gateways: action.payload };
    case 'SET_HIERARCHY':
      return { ...state, hierarchy: action.payload };
    case 'SET_SELECTED_ENTITY':
      return { ...state, selectedEntity: action.payload };
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'ADD_CUSTOMER':
      return { ...state, customers: [...state.customers, action.payload] };
    case 'UPDATE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.map(c => c.id === action.payload.id ? action.payload : c),
      };
    case 'DELETE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.filter(c => c.id !== action.payload),
      };
    case 'ADD_ASSET':
      return { ...state, assets: [...state.assets, action.payload] };
    case 'UPDATE_ASSET':
      return {
        ...state,
        assets: state.assets.map(a => a.id === action.payload.id ? action.payload : a),
      };
    case 'DELETE_ASSET':
      return {
        ...state,
        assets: state.assets.filter(a => a.id !== action.payload),
      };
    case 'ADD_DEVICE':
      return { ...state, devices: [...state.devices, action.payload] };
    case 'UPDATE_DEVICE':
      return {
        ...state,
        devices: state.devices.map(d => d.id === action.payload.id ? action.payload : d),
      };
    case 'DELETE_DEVICE':
      return {
        ...state,
        devices: state.devices.filter(d => d.id !== action.payload),
      };
    case 'ADD_GATEWAY':
      return { ...state, gateways: [...state.gateways, action.payload] };
    case 'UPDATE_GATEWAY':
      return {
        ...state,
        gateways: state.gateways.map(g => g.id === action.payload.id ? action.payload : g),
      };
    case 'DELETE_GATEWAY':
      return {
        ...state,
        gateways: state.gateways.filter(g => g.id !== action.payload),
      };
    default:
      return state;
  }
}

interface ManagementContextType {
  state: ManagementState;
  dispatch: React.Dispatch<ManagementAction>;
}

const ManagementContext = createContext<ManagementContextType | undefined>(undefined);

export function ManagementProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(managementReducer, initialState);

  return (
    <ManagementContext.Provider value={{ state, dispatch }}>
      {children}
    </ManagementContext.Provider>
  );
}

export function useManagement() {
  const context = useContext(ManagementContext);
  if (context === undefined) {
    throw new Error('useManagement must be used within a ManagementProvider');
  }
  return context;
}